import { defineStore } from 'pinia'
import eventType, { SseEvent } from '@/constants/eventType'
import { notify } from '@/utils/notify'
import { useStatsStore } from '@/store/stats'
import { useProfileStore } from '@/store/profile'
import { useCasesStore } from './cases'
import { useAppStore } from './app'
import { useGiveawaysStore } from './giveaways'
import { usePromotionalEventsStore } from './promotionalEvents'
import { useActivityFeedStore } from './activityFeed'
import { useSafeboxStore } from './safebox'
import { useArenaListStore } from './arenaList'
import { useArenaRoomStore } from './arenaRoom'
import { useSkinClashStore } from './skinClash'
import { useDepositStore } from './deposit'
import { i18n } from '@/i18n'
import { getGiveawayDetails } from '@/services/api/requests/giveaways'
// Models
import { Case } from '@/services/models/models'
import { GiveawayRoundResponse } from '@/services/models/response'

// MODELS
interface CoinPaymentStatusResponse {
  transactionId: string
  status: string
  currency: string
  amount: string
  confirmations: number
  txHash: string
  updatedAt: string
}
interface CoinPaymentCompleteResponse {
  transactionId: string
  currency: string
  cryptoAmount: string
  usdAmount: string
  originalAmount: string
  bonusMultiplier: string
  completedAt: string
}

interface GiveawayJoinPayload {
  roundId: string
  participant: {
    id: string
    name: string
    avatar?: string
  }
  participantCount: number
}

interface GiveawayEndPayload {
  roundId: string
  winner: {
    userId: string
    name: string
    avatar?: string | null
  }
}

interface GiveawayActivePayload {
  roundId: string
  endsAt: string
}

interface GiveawayPickingPayload {
  roundId: string
}

interface GiveawayCreatedPayload {
  round: any // TODO
}

// Helper function to create giveaway history entry
const createGiveawayHistoryEntry = (endedRound: any, winner: any) => ({
  id: endedRound.id,
  endedAt: endedRound.endsAt,
  giveawayType: endedRound.giveawayType,
  item: endedRound.item,
  fairness: endedRound.fairness ?? null,
  winner: {
    id: winner.id ?? winner.userId,
    name: winner.name,
    avatar: winner.avatar ?? null,
  },
})

const syncCurrentGiveawayRound = async (roundId: string) => {
  const giveawaysStore = useGiveawaysStore()
  if (giveawaysStore.$state.currentGiveawayRound?.id !== roundId) return

  try {
    const details = await getGiveawayDetails(roundId)
    giveawaysStore.setCurrentGiveawayRound(details)
    giveawaysStore.upsertGiveawayRound(details)
  } catch (error) {
    console.error('Failed to sync giveaway details after SSE update', error)
  }
}

const SKIN_CLASH_WIN_FEED_DELAY_MS = 12_000

// Adding drops with delay if tab is focused
const handleDrops = (drops, game) => {
  const profileStore = useProfileStore()
  const { profile } = profileStore
  const statsStore = useStatsStore()
  if (document.hidden) {
    statsStore.addDrops({ drops, game })
  } else {
    const waitTimeMs = 10_000
    drops.forEach((drop, index) => {
      let wait = 0
      if (drop.isArena) {
        wait = 5500
      } else if (profile) {
        wait = drop.user.id === profile.id ? waitTimeMs : 0
      }
      wait += index * 200
      setTimeout(() => {
        statsStore.addDrop({ drop, game })
      }, wait)
    })
  }
}

// Handling feed activities with delay for own actions (spoilers)
const handleActivityFeed = (payload: any) => {
  const profileStore = useProfileStore()
  const { profile } = profileStore
  const activityStore = useActivityFeedStore()

  // Default no delay
  let wait = 0

  if (payload.type === 'SKIN_CLASH_WIN') {
    wait = SKIN_CLASH_WIN_FEED_DELAY_MS
  }

  // If it's my own activity, delay it to let animations play
  if (profile && payload.user && payload.user.id === profile.id) {
    // 8 seconds seems to be a good sweet spot for upgrades/unboxing animations
    wait = Math.max(wait, 8000)
  }

  if (wait > 0) {
    setTimeout(() => {
      activityStore.addActivity(payload)
    }, wait)
  } else {
    activityStore.addActivity(payload)
  }
}

export const useSseStore = defineStore('sse', {
  state: () => ({}) as Record<string, never>,

  actions: {
    /** Generic router so the client can do: sseStore.$onEvent(type, payload) */
    $onEvent(type: SseEvent, payload: unknown) {
      const fn = (this as any)[type]
      if (typeof fn === 'function') {
        fn.call(this, payload)
      } else {
        console.warn('[SSE] Unhandled event type:', type, payload)
      }
    },

    // === Specific handlers ===

    /** Profile Update */
    [eventType.PROFILE_UPDATE](profilePayload: unknown) {
      console.debug('[SSE]', eventType.PROFILE_UPDATE, profilePayload)
      const profileStore = useProfileStore()
      profileStore.fetchProfile()
    },

    /** Stats Update */
    [eventType.STATS](statsPayload: any) {
      console.debug('[SSE]', eventType.STATS, statsPayload)
      const statsStore = useStatsStore()
      statsStore.setGeneralStats(statsPayload)
    },
    // Online Users Update
    [eventType.ONLINE](onlineUsersPayload: number) {
      const statsStore = useStatsStore()
      statsStore.setOnlineUsers(onlineUsersPayload)
    },
    // Case Update
    [eventType.CASE_UPDATE](casePayload: Case) {
      const casesStore = useCasesStore()
      casesStore.setCase(casePayload)
    },

    // Deposit Complete
    [eventType.COINPAYMENTS_DEPOSIT_COMPLETE](depositPayload: CoinPaymentCompleteResponse) {
      const toCents = (value: string) => {
        const amount = Number(value)
        return Number.isFinite(amount) ? Math.round(amount * 100) : 0
      }
      const originalAmountCents = toCents(depositPayload.originalAmount)
      const creditedAmountCents = toCents(depositPayload.usdAmount)
      const depositStore = useDepositStore()
      const appStore = useAppStore()

      depositStore.setCompletion({
        source: 'crypto',
        amountCents: originalAmountCents,
        bonusCents: Math.max(0, creditedAmountCents - originalAmountCents),
        creditedCents: creditedAmountCents,
        transactionId: depositPayload.transactionId,
        currency: depositPayload.currency,
      })
      // Surface the completed deposit without recording a second checkout-start event.
      appStore.showDepositModal = true

      const profileStore = useProfileStore()
      profileStore.fetchProfile()
    },
    // Deposit Status Update
    [eventType.COINPAYMENTS_DEPOSIT_STATUS](statusPayload: CoinPaymentStatusResponse) {
      if (statusPayload.status === 'waiting') {
        notify(
          i18n.global.t('DEPOSIT_MODAL.status.waitingConfirmations', {
            amount: statusPayload.amount,
            currency: statusPayload.currency,
          }),
          'info',
        )
      }
    },
    // New Drops
    [eventType.LIVE_DROP](dropsPayload: any) {
      console.debug('[SSE]', eventType.LIVE_DROP, dropsPayload)
      handleDrops(dropsPayload, 'caseOpening')
    },
    // Announcement
    [eventType.ANNOUNCEMENT](announcementPayload) {
      const appStore = useAppStore()
      appStore.setAnnouncement(announcementPayload.message)
      if (announcementPayload.isActive) {
        appStore.setAnnouncement(announcementPayload.text)
      } else {
        appStore.setAnnouncement('')
      }
    },

    // === ARENA ===
    [eventType.ARENA_CREATED](payload: any) {
      const arenaListStore = useArenaListStore()
      const arenaRoomStore = useArenaRoomStore()

      arenaListStore.upsertRoom(payload)
      arenaRoomStore.applyRoomUpdate(payload)
    },

    [eventType.ARENA_JOINED](payload: any) {
      const arenaListStore = useArenaListStore()
      const arenaRoomStore = useArenaRoomStore()

      if (payload?.room) {
        arenaListStore.upsertRoom(payload.room)
        arenaRoomStore.applyRoomUpdate(payload.room)
      }
      arenaRoomStore.applyParticipantJoined(payload)
    },

    [eventType.ARENA_PARTICIPANT_READY](payload: any) {
      const arenaListStore = useArenaListStore()
      const arenaRoomStore = useArenaRoomStore()

      if (!payload?.roomId || !payload?.participantId) return
      arenaListStore.setParticipantReady(payload.roomId, payload.participantId)
      arenaRoomStore.applyParticipantReady(payload.roomId, payload.participantId)
    },

    [eventType.ARENA_UPDATE](payload: any) {
      const arenaListStore = useArenaListStore()
      const arenaRoomStore = useArenaRoomStore()

      arenaListStore.upsertRoom(payload)
      arenaRoomStore.applyRoomUpdate(payload)
    },

    [eventType.SKIN_CLASH_JOIN](payload: any) {
      const skinClashStore = useSkinClashStore()
      skinClashStore.applyRoundUpdate(payload)
    },

    [eventType.SKIN_CLASH_ACTIVE](payload: any) {
      const skinClashStore = useSkinClashStore()
      skinClashStore.applyRoundUpdate(payload)
    },

    [eventType.SKIN_CLASH_END](payload: any) {
      const skinClashStore = useSkinClashStore()
      skinClashStore.applyRoundUpdate(payload)
      skinClashStore.fetchHistory()
    },

    [eventType.ARENA_START](payload: any) {
      const roomId = typeof payload === 'string' ? payload : payload?.roomId
      if (!roomId) return

      const arenaRoomStore = useArenaRoomStore()
      arenaRoomStore.applyArenaStart(roomId)
    },

    [eventType.ARENA_ROUND](payload: any) {
      const arenaRoomStore = useArenaRoomStore()
      arenaRoomStore.applyArenaRound(payload)
    },

    [eventType.ARENA_FINISHED](payload: any) {
      const arenaListStore = useArenaListStore()
      const arenaRoomStore = useArenaRoomStore()

      if (payload?.roomId) {
        arenaListStore.patchRoom(payload.roomId, { status: 'COMPLETED' })
      }
      arenaRoomStore.applyArenaFinished(payload)
    },

    // === GIVEAWAYS ===
    // New Active Giveaway - When endsAt is updated or a new giveaway is active
    [eventType.GIVEAWAY_ACTIVE](giveawayPayload: any) {
      const giveawaysStore = useGiveawaysStore()
      const relatedGiveaway = giveawaysStore.$state.giveawayRounds.find(
        (r) => r.id === giveawayPayload.roundId,
      )

      if (!relatedGiveaway) {
        giveawaysStore.refreshGiveawaysSilently()
      } else {
        relatedGiveaway.endsAt = giveawayPayload.endsAt
      }

      // Update currentGiveawayRound if it matches
      if (giveawaysStore.$state.currentGiveawayRound?.id === giveawayPayload.roundId) {
        giveawaysStore.$state.currentGiveawayRound.endsAt = giveawayPayload.endsAt
        giveawaysStore.$state.currentGiveawayRound.status = GiveawayRoundResponse.status._10
      }

      void syncCurrentGiveawayRound(giveawayPayload.roundId)
    },
    [eventType.GIVEAWAY_JOIN](giveawayJoinPayload: GiveawayJoinPayload) {
      const giveawaysStore = useGiveawaysStore()
      const profileStore = useProfileStore()

      const relatedGiveaway = giveawaysStore.$state.giveawayRounds.find(
        (r) => r.id === giveawayJoinPayload.roundId,
      )
      if (relatedGiveaway) {
        relatedGiveaway.participantCount = giveawayJoinPayload.participantCount

        // Check if current user joined (only if authenticated)
        if (
          profileStore.profile &&
          profileStore.profile.id === giveawayJoinPayload.participant.id
        ) {
          relatedGiveaway.userJoined = true
        }
      }

      // Update currentGiveawayRound if it matches
      if (giveawaysStore.$state.currentGiveawayRound?.id === giveawayJoinPayload.roundId) {
        giveawaysStore.$state.currentGiveawayRound.participantCount =
          giveawayJoinPayload.participantCount

        // Check if current user joined (only if authenticated)
        if (
          profileStore.profile &&
          profileStore.profile.id === giveawayJoinPayload.participant.id
        ) {
          giveawaysStore.$state.currentGiveawayRound.userJoined = true
        }

        // Add participant to the participants list if it exists
        if ((giveawaysStore.$state.currentGiveawayRound as any).participants) {
          ; (giveawaysStore.$state.currentGiveawayRound as any).participants.push({
            name: giveawayJoinPayload.participant.name,
            avatar: giveawayJoinPayload.participant.avatar || '',
          })
        }
      }

      // Trigger floating avatar animation
      const event = new CustomEvent('giveaway-join-animation', {
        detail: {
          roundId: giveawayJoinPayload.roundId,
          participant: giveawayJoinPayload.participant,
        },
      })
      window.dispatchEvent(event)
    },
    // Giveaway END - When a giveaway ends
    [eventType.GIVEAWAY_END](giveawayEndPayload: GiveawayEndPayload) {
      const giveawaysStore = useGiveawaysStore()
      const relatedGiveaway = giveawaysStore.$state.giveawayRounds.find(
        (r) => r.id === giveawayEndPayload.roundId,
      )
      if (relatedGiveaway) {
        relatedGiveaway.status = GiveawayRoundResponse.status._30
      }

      // Update currentGiveawayRound if it matches
      if (giveawaysStore.$state.currentGiveawayRound?.id === giveawayEndPayload.roundId) {
        giveawaysStore.$state.currentGiveawayRound.status = GiveawayRoundResponse.status._30
          ; (giveawaysStore.$state.currentGiveawayRound as any).winner = {
            id: giveawayEndPayload.winner.userId,
            name: giveawayEndPayload.winner.name,
            avatar: giveawayEndPayload.winner.avatar ?? null,
          }
      }

      // Add to winners list
      const winnerData = {
        roundId: giveawayEndPayload.roundId,
        winner: {
          id: giveawayEndPayload.winner.userId,
          name: giveawayEndPayload.winner.name,
          avatar: giveawayEndPayload.winner.avatar ?? null,
        },
      }
      giveawaysStore.addWinner(winnerData)

      void syncCurrentGiveawayRound(giveawayEndPayload.roundId)
    },
    // Giveaway PICKING - When giveaway is picking a winner
    [eventType.GIVEAWAY_PICKING](giveawayPickingPayload: GiveawayPickingPayload) {
      const giveawaysStore = useGiveawaysStore()
      const relatedGiveaway = giveawaysStore.$state.giveawayRounds.find(
        (r) => r.id === giveawayPickingPayload.roundId,
      )
      if (relatedGiveaway) {
        relatedGiveaway.status = GiveawayRoundResponse.status._20
      }

      // Update currentGiveawayRound if it matches
      if (giveawaysStore.$state.currentGiveawayRound?.id === giveawayPickingPayload.roundId) {
        giveawaysStore.$state.currentGiveawayRound.status = GiveawayRoundResponse.status._20
      }
    },
    // Giveaway CREATED - When a new giveaway is created
    [eventType.GIVEAWAY_CREATED](giveawayCreatedPayload: GiveawayCreatedPayload) {
      const giveawaysStore = useGiveawaysStore()

      setTimeout(() => {
        const existingIndex = giveawaysStore.$state.giveawayRounds.findIndex(
          (r) => r.giveawayType.name === giveawayCreatedPayload.round.giveawayType.name,
        )
        if (existingIndex === -1) {
          giveawaysStore.upsertGiveawayRound(giveawayCreatedPayload.round)
          return
        }

        const endedRound = giveawaysStore.$state.giveawayRounds[existingIndex]
        const winner = giveawaysStore.getWinnerByRoundId(endedRound.id)
        if (winner && endedRound.item) {
          const historyEntry = createGiveawayHistoryEntry(endedRound, winner)

          giveawaysStore.$state.history.recent = [
            historyEntry,
            ...giveawaysStore.$state.history.recent,
          ].slice(0, 20) // Keep last 20 recent entries
        }

        // Replace with new round
        giveawaysStore.upsertGiveawayRound(giveawayCreatedPayload.round)
      }, 8000) // 8 second delay to ensure winner animation completes
    },

    // === SKINS DEPOSIT ===
    [eventType.SKINSBACK_DEPOSIT_INSTANT_CREDIT](payload: any) {
      notify(
        `You have received an instant credit of $${(payload.instantCredit / 100).toFixed(2)} for your deposit! It has been added to your balance.`,
        'success',
      )
      const profileStore = useProfileStore()
      profileStore.fetchProfile()
    },

    // === PROMOTIONAL EVENTS ===
    [eventType.PROMOTIONAL_EVENT_ACTIVATED](payload: any) {
      console.debug('[SSE]', eventType.PROMOTIONAL_EVENT_ACTIVATED, payload)
      const eventsStore = usePromotionalEventsStore()
      eventsStore.onEventActivated(payload.event)
      notify(`New event started: ${payload.event.name}!`, 'info')
    },

    [eventType.PROMOTIONAL_EVENT_ENDED](payload: any) {
      console.debug('[SSE]', eventType.PROMOTIONAL_EVENT_ENDED, payload)
      const eventsStore = usePromotionalEventsStore()
      eventsStore.onEventEnded()
      notify(`Event "${payload.name}" has ended!`, 'info')
    },

    [eventType.WHALE_POINTS_AWARDED](payload: any) {
      console.debug('[SSE]', eventType.WHALE_POINTS_AWARDED, payload)
      const eventsStore = usePromotionalEventsStore()
      const profileStore = useProfileStore()

      // Update user stats if this is for the current user
      if (profileStore.profile?.id === payload.userId) {
        eventsStore.updateWPStats({
          totalEarned: payload.newTotalEarned,
          available: payload.newAvailable,
        })

        // Show WP earned notification
        notify(`+${payload.whalePoints} Whale Points earned!`, 'success')

        // Trigger animation
        const event = new CustomEvent('wp-earned', {
          detail: {
            amount: payload.whalePoints,
            newTotal: payload.newAvailable,
          },
        })
        window.dispatchEvent(event)
      }
    },

    [eventType.LEADERBOARD_UPDATED](payload: any) {
      console.debug('[SSE]', eventType.LEADERBOARD_UPDATED, payload)
      const eventsStore = usePromotionalEventsStore()
      if (payload.topPositions) {
        eventsStore.updateLeaderboard(payload.topPositions)
      }
    },

    [eventType.PROMOTIONAL_EVENT_WP_UPDATE](payload: { eventId: string; whalePoints: number }) {
      console.debug('[SSE]', eventType.PROMOTIONAL_EVENT_WP_UPDATE, payload)

      const profileStore = useProfileStore()

      // Update the whalePoints in profile
      if (profileStore.profile) {
        profileStore.profile.whalePoints = payload.whalePoints
      }

      // Trigger WP pill animation
      const event = new CustomEvent('wp-update', {
        detail: {
          whalePoints: payload.whalePoints,
        },
      })
      window.dispatchEvent(event)
    },

    // === SAFEBOX (WHALE PROTOCOL) ===
    [eventType.SAFEBOX_ATTEMPT](payload: any) {
      const safeboxStore = useSafeboxStore()

      // Update jackpot if provided
      if (payload.jackpot) {
        safeboxStore.updateJackpot(payload.jackpot)
      }

      // Add attempt
      safeboxStore.addAttempt({
        id: payload.id,
        user: {
          id: payload.userId,
          name: payload.name,
          avatar: payload.avatar,
          isVip: payload.isVip || false,
          eventTeam: payload.eventTeam || null
        },
        createdAt: payload.createdAt,
        bonusWon: payload.wonBonus
      })
    },

    // === ACTIVITY FEED ===
    [eventType.ACTIVITY_FEED](payload: any) {
      // console.debug('[SSE]', eventType.ACTIVITY_FEED, payload)
      handleActivityFeed(payload)
    },
  },
})
